import { client } from "../api/generated/client.gen";
import axios, { AxiosResponse } from "axios";
import { useEffect } from "react";
import { Modal, notification } from "antd";
import { CV_CONTEXT_PATH, getToken, logOut } from "../security";
import { apiUrl } from "../app.config";
import { getRemoteIp } from "../common/helpers/remoteIp";

interface ErrorMessage {
  message: string;
}

export const useClient = () => {
  const openNotification = (errorMessage: ErrorMessage) => {
    notification.error({
      message: "Sesión Expirada",
      description: errorMessage.message,
      onClose: () => {
        logOut();
      },
    });
  };
  const openNotificationSubscription = (
    errorMessage: ErrorMessage,
    logout = false
  ) => {
    if (!logout) {
      Modal.error({
        title: "Error en suscripción",
        content: errorMessage.message,
        onOk: () => {
          window.location.replace(`${CV_CONTEXT_PATH}/subscription`);
        },
        onCancel: () => {
          window.location.replace(`${CV_CONTEXT_PATH}/subscription`);
        },
      });
    } else {
      notification.error({
        message: "Error en suscripción",
        description: errorMessage.message,
        onClose: () => (logout ? logOut() : undefined),
      });
    }
  };

  useEffect(() => {
    client.setConfig({
      baseURL: apiUrl,
      throwOnError: true,
    });

    // Add interceptors
    const requestInterceptor = client.instance.interceptors.request.use(
      async (config) => {
        config.headers.set("Authorization", getToken());
        getRemoteIp().then((ip: string) => {
          config.headers.set("originIp", ip);
        });

        return config;
      }
    );

    // Add response interceptor
    const responseInterceptor = client.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        if (getToken() === null) {
          return error;
        }
        if (axios.isCancel(error)) {
          return Promise.reject("Petición Cancelada");
        }

        if (axios.isAxiosError(error)) {
          // Check for network errors (no response from server)
          if (!error.response) {
            // This is a network error (e.g., connection refused, DNS error, offline)
            openNotification({
              message: "No se ha podido conectar con el servidor",
            });
            return Promise.reject(error);
          }

          switch (error.status) {
            case 410:
            case 401:
              openNotification(error.response.data);
              break;
            case 412:
              openNotificationSubscription(error.response.data, true);
              break;
            case 409:
              openNotificationSubscription(error.response.data);
              break;
            default:
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      client.instance.interceptors.request.eject(requestInterceptor);
      client.instance.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  return { client };
};
